import * as React from "react";
import { DataTable } from "components/DataTable/DataTable";
import { ApiError } from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

interface PromptItem {
  key: keyof import("gen/clients").AiPrompts;
  name: string;
  description: string;
  value: string;
}

interface Props {
  prompts: PromptItem[];
  onEditClick: (prompt: PromptItem) => void;
  isUpdating: boolean;
  updateError: ApiError | null;
}

const AiPromptsTable: React.FC<Props> = ({ prompts, onEditClick, isUpdating, updateError }) => {
  const truncateText = (text: string, maxLength = 100): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  const rowActions = React.useCallback(
    (prompt: PromptItem) => [
      {
        title: "Edit",
        action: () => onEditClick(prompt)
      }
    ],
    [onEditClick]
  );

  return (
    <DataTable
      columns={[
        {
          id: "name",
          title: "Prompt Name",
          cell: (prompt: PromptItem) => (
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {prompt.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {prompt.description}
              </Typography>
            </Box>
          )
        },
        {
          id: "value",
          title: "Prompt Text",
          cell: (prompt: PromptItem) => (
            <Tooltip title={prompt.value} placement="top-start">
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  whiteSpace: "pre-wrap",
                  cursor: "help"
                }}
              >
                {truncateText(prompt.value, 150)}
              </Typography>
            </Tooltip>
          )
        }
      ]}
      data={prompts}
      isLoading={isUpdating}
      error={updateError && <ApiErrorMessage apiError={updateError} />}
      rowKey={(prompt: PromptItem) => prompt.key}
      rowActions={rowActions}
      dense={false}
    />
  );
};

export { AiPromptsTable };
